import { useEffect, useState } from "react";
import { Sparkles, Check, Loader2 } from "lucide-react";

const STEPS = [
  "Obteniendo contenido y metadatos del video",
  "Analizando hook, ritmo y retención",
  "Evaluando SEO, storytelling y CTAs",
  "Generando insights y recomendaciones",
];

const Sk = ({ className = "" }) => <div className={`vt-skeleton ${className}`} aria-hidden="true" />;

export function SkeletonCard({ lines = 3, className = "" }) {
  return (
    <div className={`vt-card bg-brand-card p-5 ${className}`}>
      <div className="flex items-center gap-3 mb-4"><Sk className="w-9 h-9 rounded-xl" /><Sk className="h-4 w-40" /></div>
      {Array.from({ length: lines }).map((_, i) => <Sk key={i} className={`h-3 mb-2.5 ${i === lines - 1 ? "w-2/3" : "w-full"}`} />)}
    </div>
  );
}

export function SkeletonList({ rows = 4 }) {
  return (
    <div className="space-y-3" data-testid="skeleton-list">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="vt-card bg-brand-card p-4 flex items-center gap-4">
          <Sk className="w-12 h-12 rounded-xl shrink-0" />
          <div className="flex-1"><Sk className="h-3.5 w-1/2 mb-2" /><Sk className="h-3 w-1/3" /></div>
          <Sk className="h-8 w-24 rounded-lg" />
        </div>
      ))}
    </div>
  );
}

export function StepIndicator({ startedAt }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      setStep(Math.min(STEPS.length - 1, Math.floor(elapsed / 9)));
    }, 500);
    return () => clearInterval(t);
  }, [startedAt]);
  return (
    <ol data-testid="loading-steps" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
      {STEPS.map((label, i) => {
        const done = i < step, active = i === step;
        return (
          <li key={label} className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm transition-colors duration-300 ${active ? "border-brand-red/50 bg-brand-red/10 text-vt-ink shadow-[0_0_16px_-4px_rgba(224,0,0,0.45)]" : done ? "border-emerald-400/30 bg-emerald-500/5 text-brand-text2" : "border-white/10 text-brand-muted"}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-emerald-500/15 text-emerald-300" : active ? "bg-brand-red/20 text-brand-red" : "bg-white/5"}`}>
              {done ? <Check className="w-3.5 h-3.5" /> : active ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[10px] font-bold">{i + 1}</span>}
            </span>
            <span className="leading-tight">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export default function ReportSkeleton({ startedAt, title = "Generando tu informe con IA", subtitle = "Suele tardar entre 30 y 45 segundos." }) {
  return (
    <div data-testid="report-skeleton" className="space-y-6 animate-fadeIn" aria-busy="true" aria-live="polite">
      <div className="vt-glass rounded-2xl p-5 border border-purple-400/25">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-400/40 text-purple-300 flex items-center justify-center shadow-[0_0_18px_rgba(139,92,246,0.4)]">
            <Sparkles className="w-5 h-5 animate-pulse" />
          </span>
          <div>
            <p className="font-bold text-vt-ink">{title}</p>
            <p className="text-xs text-brand-muted">{subtitle}</p>
          </div>
        </div>
        {startedAt && <StepIndicator startedAt={startedAt} />}
      </div>

      <div className="rounded-3xl border border-white/10 bg-brand-card p-6 md:p-10 grid lg:grid-cols-[auto_1fr] gap-8 items-center">
        <div className="relative w-[200px] h-[200px] mx-auto">
          <div className="absolute inset-0 rounded-full border-[14px] border-white/5" />
          <div className="absolute inset-0 rounded-full border-[14px] border-transparent border-t-purple-500/70 border-r-pink-500/50 animate-spin [animation-duration:2.2s]" />
          <div className="absolute inset-0 flex items-center justify-center"><Sk className="h-10 w-16 rounded-lg" /></div>
        </div>
        <div className="space-y-3"><Sk className="h-3 w-32" /><Sk className="h-7 w-64" /><Sk className="h-3 w-48" /><Sk className="h-14 w-72 rounded-2xl mt-4" /></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="vt-card bg-brand-card p-4">
            <div className="flex items-center justify-between mb-3"><Sk className="w-9 h-9 rounded-xl" /><Sk className="h-6 w-12" /></div>
            <Sk className="h-3 w-20 mb-2.5" /><Sk className="h-1.5 w-full rounded-full" />
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <SkeletonCard lines={4} /><SkeletonCard lines={4} />
      </div>
    </div>
  );
}
